import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';

const Advantages = () => {
  const { t } = useTranslation();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const advantages = [
    {
      titleKey: 'advantages.item1.title',
      descKey: 'advantages.item1.desc',
      color: 'from-blue-500 to-cyan-500',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
        </svg>
      )
    },
    {
      titleKey: 'advantages.item2.title',
      descKey: 'advantages.item2.desc',
      color: 'from-green-500 to-emerald-500',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      )
    },
    {
      titleKey: 'advantages.item3.title',
      descKey: 'advantages.item3.desc',
      color: 'from-purple-500 to-pink-500',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
      )
    },
    {
      titleKey: 'advantages.item4.title',
      descKey: 'advantages.item4.desc',
      color: 'from-orange-500 to-red-500',
      icon: (
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
        </svg>
      )
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { 
      opacity: 0, 
      y: 40
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <section id="advantages" className="py-20 relative overflow-hidden" ref={ref}>
      {/* Enhanced Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-800 via-purple-900/20 to-slate-900" />
      
      {/* Color Accent Overlays */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-0 w-96 h-96 bg-blue-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-orange-500/5 rounded-full blur-3xl" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_70%,rgba(168,85,247,0.1),transparent_50%)]" />
        
        {/* Enhanced Grid Pattern with Color */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(168,85,247,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(168,85,247,0.03)_1px,transparent_1px)] [background-size:60px_60px]" />

        {/* Enhanced Professional Corner Element */}
        <motion.div
          className="absolute top-20 right-20 w-16 h-16 opacity-20"
          animate={{
            rotate: [0, 180, 360],
          }}
          transition={{
            duration: 30,
            repeat: Infinity,
            ease: "linear"
          }}
        >
          <svg viewBox="0 0 24 24" fill="none" className="w-full h-full text-purple-400">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2" stroke="currentColor" strokeWidth="1"/>
            <path d="M9 9L15 15M15 9L9 15" stroke="currentColor" strokeWidth="1"/>
          </svg>
        </motion.div>

        {/* Enhanced Floating Elements */}
        {[...Array(8)].map((_, i) => (
          <motion.div
            key={i}
            className={`absolute w-1 h-1 rounded-full opacity-40 ${
              i % 4 === 0 ? 'bg-blue-400' : 
              i % 4 === 1 ? 'bg-purple-400' : 
              i % 4 === 2 ? 'bg-green-400' : 'bg-orange-400'
            }`}
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -15, 0],
              opacity: [0.4, 0.8, 0.4],
            }}
            transition={{
              duration: Math.random() * 5 + 4,
              repeat: Infinity,
              delay: Math.random() * 3,
            }}
          />
        ))}
      </div>

      <div className="max-w-7xl mx-auto px-6 relative z-10">
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          {/* Enhanced Section Title */}
          <motion.div
            variants={cardVariants}
            className="text-center mb-16"
          >
            <motion.h2 
              className="text-3xl md:text-4xl font-bold text-white mb-4 relative inline-block"
            >
              {t('advantages.title')}
              <motion.div
                className="absolute -bottom-2 left-0 right-0 h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-orange-500 rounded-full"
                initial={{ scaleX: 0 }}
                animate={isInView ? { scaleX: 1 } : { scaleX: 0 }}
                transition={{ duration: 0.8, delay: 0.5 }}
              />
            </motion.h2>
          </motion.div>

          {/* Enhanced Advantages Grid */}
          <motion.div 
            className="grid md:grid-cols-2 gap-8"
            variants={containerVariants}
          >
            {advantages.map((advantage, index) => (
              <motion.div
                key={advantage.titleKey}
                variants={cardVariants}
                className="group relative"
                whileHover={{ 
                  y: -5,
                  transition: { duration: 0.2 }
                }}
              >
                <motion.div
                  className="bg-slate-800/60 p-8 rounded-lg backdrop-blur-lg border border-slate-700 hover:border-slate-600 transition-all duration-300 relative overflow-hidden"
                >
                  {/* Enhanced Gradient Accent */}
                  <div className={`absolute inset-0 bg-gradient-to-r ${advantage.color} opacity-5 group-hover:opacity-10 transition-opacity duration-300`} />
                  
                  {/* Enhanced Icon Container */}
                  <motion.div 
                    className="mb-6 relative z-10"
                    whileHover={{ 
                      scale: 1.05,
                      transition: { duration: 0.2 }
                    }}
                  >
                    <motion.div
                      className={`inline-flex p-4 rounded-lg bg-gradient-to-r ${advantage.color} text-white border border-slate-600 shadow-lg`}
                    >
                      {advantage.icon}
                    </motion.div>
                  </motion.div>

                  {/* Enhanced Title */}
                  <motion.h3 
                    className={`text-xl font-bold mb-4 relative z-10 text-transparent bg-gradient-to-r ${advantage.color} bg-clip-text`}
                  >
                    {t(advantage.titleKey)}
                  </motion.h3>

                  {/* Description */}
                  <motion.p 
                    className="text-slate-300 leading-relaxed relative z-10"
                  >
                    {t(advantage.descKey)}
                  </motion.p>

                  {/* Enhanced Index Number */}
                  <motion.div
                    className={`absolute bottom-4 right-4 text-4xl font-bold opacity-20 group-hover:opacity-30 transition-opacity duration-300 text-transparent bg-gradient-to-r ${advantage.color} bg-clip-text`}
                  >
                    0{index + 1}
                  </motion.div>

                  {/* Decorative Corner Element */}
                  <motion.div
                    className={`absolute top-4 right-4 w-12 h-12 opacity-10 group-hover:opacity-20 transition-opacity duration-300`}
                    animate={{
                      rotate: [0, 360],
                    }}
                    transition={{
                      duration: 20,
                      repeat: Infinity,
                      ease: "linear"
                    }}
                  >
                    <div className={`w-full h-full border border-current rounded-full text-transparent bg-gradient-to-r ${advantage.color} bg-clip-text`} />
                  </motion.div>
                </motion.div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Advantages;